import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios'
import { API_BASE_URL } from './constants'
import { handleApiError } from './utils/errorHandler'
import { tokenManager } from './utils/tokenManager'
import { urlBuilder } from './utils/urlBuilder'
import type { ApiResponse, ApiError, RequestConfig, ErrorResponseData } from './types'
import { User } from '../types/auth'
import MessageBox from '../services/MessageBox'
//import { eventTracker } from '../services/EventTracker'

export interface ExtendedRequestConfig extends RequestConfig {
  skipLoading?: boolean; // Add skipLoading as an optional property
  admin?: boolean;
  hideError?: boolean;
}

export class Api {
  private static instance: Api
  private axios: AxiosInstance
  private loadingStore: any = null

  private constructor() {
    this.axios = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
        //'Accept': '*/*',
      },
    })

    // Request interceptor
    this.axios.interceptors.request.use(
      (config) => {
        /*eventTracker.trackApiCall(
          config.url || 'unknown',
          config.method?.toUpperCase() || 'unknown',
          config.data
        )*/
        const extendedConfig = config as ExtendedRequestConfig;
        if (this.loadingStore && extendedConfig.skipLoading != true) {
          this.loadingStore.startLoading()
        }
        config.headers.Authorization = `Bearer ${tokenManager.get()}`;
        config.headers.AccessKey = tokenManager.getCompany()?.uid
        config.headers.maxBodyLength = 'Infinity'
        return config;
      },
      (error) => {
        if (this.loadingStore) {
          this.loadingStore.stopLoading()
        }
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.axios.interceptors.response.use(
      (response): AxiosResponse => {
        if (this.loadingStore) {
          this.loadingStore.stopLoading()
        }
        return response;
      },
      (error: AxiosError<ErrorResponseData>): Promise<ApiError> => {
        if (error.status == 401) {
          tokenManager.logout()
        }

        const apiError = handleApiError(error)

        if (this.loadingStore) {
          this.loadingStore.stopLoading()
        }

        // Handle validation errors (422)
        if (error.response?.status === 422 || error.code == '422') {
          const validationErrors: Record<string, string[]> = (error.response?.data as { errors?: Record<string, string[]> })?.errors || {};
          console.log('valid errors', validationErrors)
          const errorMessages = Object.entries(validationErrors)
            .map(([, messages]) => `• ${(messages as string[]).join(', ')}<br />`)
            .join('\n')
          console.log('errorMessages', errorMessages)
          if (!(error.config as ExtendedRequestConfig)?.hideError) {
            MessageBox.show(errorMessages, 'error')
          }
        } else {
          // Handle other errors
          if (!(error.config as ExtendedRequestConfig)?.hideError) {
            MessageBox.show(apiError.message, 'error')
          }
        }
        
        const errorResponse: ApiError = {
          message: (error.response?.data as { message?: string })?.message || 'An unexpected error occurred',
          code: error.response?.status?.toString()
        };
        return Promise.reject(errorResponse);
      }
    );
  }

  public static getInstance(): Api {
    if (!Api.instance) {
      Api.instance = new Api()
    }
    return Api.instance
  }

  public setLoadingStore(store: any) {
    this.loadingStore = store
  }

  public async post<T>(url: string, data?: any, config?: ExtendedRequestConfig): Promise<ApiResponse<T>> {
    const properUrl = this.getProperUrl(url, config?.admin);
    try {
      if (!data) {
        data = {'t' : 'none'}
      }
      const response = await this.axios.post<T>(urlBuilder.api(properUrl), data, config);
      return this.createApiResponse(response);
    } catch (error) {
      return this.createErrorResponse(error);
    }
  }
  
  private getProperUrl(url: string, isAdmin?: boolean): string {
    if ((url.includes('clients') && !isAdmin) || url.startsWith('/')) {
      return url;
    }
    return isAdmin ? url : `/clients/${url}`;
  }
  
  private createApiResponse<T>(response: any): ApiResponse<T> {
    if (response.status === 500 || response.status === 401) {
      return this.createErrorResponse(response);
    }
  
    return {
      data: response.data.data,
      lists: response.data.lists || null,
      ocr: response.data.ocr || null,
      success: response.data.success,
      message: response.data.message,
      errors: response.data.errors,
      code: response.status
    };
  }
  
  private createErrorResponse(error: any): ApiResponse<any> {
    return {
      data: null,
      lists: null,
      ocr: null,
      success: false,
      message: error.message,
      errors: error.errors,
      code: error.code || error.response?.status
    };
  }

  public setToken(token: string): void {
    tokenManager.set(token)
  }

  public setUser(user: User): void {
    tokenManager.setUser(user)
  }

  public clearToken(): void {
    tokenManager.remove()
  }
}

// Export a singleton instance
export const api = Api.getInstance()